type LogLevel = 'error' | 'warn' | 'log' | 'info' | 'debug';

interface LoggerConfig {
  isDevelopment: boolean;
  isLoggingEnabled: boolean;
  logLevel: LogLevel;
}

class Logger {
  private config: LoggerConfig;
  private readonly STORAGE_KEY = 'pc_center_logs_enabled';
  private readonly LEVEL_KEY = 'pc_center_log_level';

  constructor() {
    const isDevelopment = process.env.NODE_ENV !== 'production';

    // In development, always enable logs
    // In production, Terser will remove all console statements during build
    // But we still check localStorage for runtime control if needed
    const storedEnabled = localStorage.getItem(this.STORAGE_KEY);
    const storedLevel = localStorage.getItem(this.LEVEL_KEY) as LogLevel;

    this.config = {
      isDevelopment,
      isLoggingEnabled: isDevelopment || storedEnabled === 'true',
      logLevel: storedLevel || 'warn'
    };
  }

  // Method to enable/disable logging at runtime
  public setEnabled(enabled: boolean): void {
    this.config.isLoggingEnabled = enabled;
    if (enabled) {
      localStorage.setItem(this.STORAGE_KEY, 'true');
    } else {
      localStorage.removeItem(this.STORAGE_KEY);
    }
  }

  // Method to set log level at runtime
  public setLogLevel(level: LogLevel): void {
    this.config.logLevel = level;
    localStorage.setItem(this.LEVEL_KEY, level);
  }

  // Method to get current logging status
  public isEnabled(): boolean {
    return this.config.isLoggingEnabled;
  }

  // Method to get current log level
  public getLogLevel(): LogLevel {
    return this.config.logLevel;
  }

  private shouldLog(level: LogLevel): boolean {
    if (!this.config.isLoggingEnabled) return false;

    const levels: LogLevel[] = ['error', 'warn', 'log', 'info', 'debug'];
    const currentLevelIndex = levels.indexOf(this.config.logLevel);
    const messageLevelIndex = levels.indexOf(level);

    return messageLevelIndex <= currentLevelIndex;
  }

  private formatMessage(level: string, args: any[]): any[] {
    const timestamp = new Date().toISOString();
    const prefix = `[${timestamp}] [${level.toUpperCase()}]`;

    // Add CSS styling for browser console
    if (typeof window !== 'undefined' && this.config.isDevelopment) {
      const colors = {
        error: 'color: #ff0000; font-weight: bold;',
        warn: 'color: #ff9800; font-weight: bold;',
        log: 'color: #2196F3;',
        info: 'color: #4CAF50;',
        debug: 'color: #9E9E9E;'
      };

      return [`%c${prefix}`, colors[level as LogLevel] || '', ...args];
    }

    return [prefix, ...args];
  }

  error(...args: any[]): void {
    if (this.shouldLog('error')) {
      console.error(...this.formatMessage('error', args));
    }
  }

  warn(...args: any[]): void {
    if (this.shouldLog('warn')) {
      console.warn(...this.formatMessage('warn', args));
    }
  }

  log(...args: any[]): void {
    if (this.shouldLog('log')) {
      console.log(...this.formatMessage('log', args));
    }
  }

  info(...args: any[]): void {
    if (this.shouldLog('info')) {
      console.info(...this.formatMessage('info', args));
    }
  }

  debug(...args: any[]): void {
    if (this.shouldLog('debug')) {
      console.debug(...this.formatMessage('debug', args));
    }
  }

  // Group methods for better organization in console
  group(label: string): void {
    if (this.config.isLoggingEnabled && console.group) {
      console.group(label);
    }
  }

  groupEnd(): void {
    if (this.config.isLoggingEnabled && console.groupEnd) {
      console.groupEnd();
    }
  }

  // Table method for structured data
  table(data: any): void {
    if (this.config.isLoggingEnabled && console.table) {
      console.table(data);
    }
  }

  // Time tracking methods
  time(label: string): void {
    if (this.config.isLoggingEnabled && console.time) {
      console.time(label);
    }
  }

  timeEnd(label: string): void {
    if (this.config.isLoggingEnabled && console.timeEnd) {
      console.timeEnd(label);
    }
  }
}

// Export singleton instance
const logger = new Logger();
export default logger;